import { useCallback, useEffect, useRef, useState } from "react";
import { billApi, paymentApi } from "../api/api";
import { mapBill, mapPayment, mapPaymentSummary } from "../api/mappers";

/* One booking's live bill and its payments.

   Every write answers with the whole bill, which replaces ours — nothing is
   patched locally, so the screen can never show a total the server did not
   compute. Writes carry the bill's `version`; if another admin changed the
   bill in between, the server refuses (BILL_CHANGED), and we reload and say
   so rather than retrying over their change.

   `versionRef` tracks the newest version seen, so several writes in a row
   (one per day of the meals grid) each send the version the previous one
   produced instead of the one captured when the screen rendered. */
export default function useBill(bookingId, { enabled = true } = {}) {
  const [bill, setBillState] = useState(null);
  const [noBill, setNoBill] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const versionRef = useRef(null);
  const current = useRef(bookingId);

  const setBill = useCallback((data) => {
    const mapped = data ? mapBill(data) : null;
    versionRef.current = mapped?.version ?? null;
    setBillState(mapped);
    if (mapped) {
      setNoBill(false);
      setPaymentSummary(mapped.paymentSummary);
    }
    return mapped;
  }, []);

  /* The bill and the booking's payments together: payments exist without a
     bill (an advance at check-in), so the card shows them either way. */
  const load = useCallback(async () => {
    current.current = bookingId;
    if (!bookingId || !enabled) return;
    setLoading(true);
    const [billRes, payRes] = await Promise.allSettled([billApi.get(bookingId), paymentApi.list(bookingId)]);
    if (current.current !== bookingId) return;
    if (billRes.status === "fulfilled") {
      setBill(billRes.value);
    } else if (billRes.reason?.code === "NO_BILL") {
      setBill(null);
      setNoBill(true);
    } else {
      setError(billRes.reason?.message || "Could not load the bill.");
    }
    if (payRes.status === "fulfilled") {
      setPayments(payRes.value.payments.map(mapPayment));
      setPaymentSummary(mapPaymentSummary(payRes.value.summary));
    }
    setLoading(false);
  }, [bookingId, enabled, setBill]);

  const loadPayments = useCallback(async () => {
    if (!bookingId || !enabled) return;
    try {
      const data = await paymentApi.list(bookingId);
      if (current.current !== bookingId) return;
      setPayments(data.payments.map(mapPayment));
      setPaymentSummary(mapPaymentSummary(data.summary));
    } catch (e) {
      if (current.current === bookingId) setError(e.message || "Could not load payments.");
    }
  }, [bookingId, enabled]);

  useEffect(() => {
    load();
    return () => { current.current = null; };
  }, [load]);

  /* Runs one bill write. Resolves to true when it succeeded. */
  const run = useCallback(async (call) => {
    setBusy(true);
    setError("");
    try {
      const data = await call(versionRef.current);
      if (current.current === bookingId) setBill(data);
      return true;
    } catch (e) {
      if (current.current !== bookingId) return false;
      if (e.code === "BILL_CHANGED") {
        await load();
        setError("Someone else changed this bill a moment ago. It has been reloaded — please check it and make your change again.");
      } else {
        setError(e.message || "That did not work.");
      }
      return false;
    } finally {
      setBusy(false);
    }
  }, [bookingId, load, setBill]);

  const b = bookingId;
  const actions = {
    create:          () => run(() => billApi.create(b)),
    refresh:         () => run((v) => billApi.refresh(b, v)),
    update:          (data) => run((v) => billApi.update(b, v, data)),
    addLine:         (data) => run((v) => billApi.addLine(b, v, data)),
    updateLine:      (lineId, data) => run((v) => billApi.updateLine(b, v, lineId, data)),
    deleteLine:      (lineId) => run((v) => billApi.deleteLine(b, v, lineId)),
    decideRetention: (data) => run((v) => billApi.retention(b, v, data)),
    setBoardingLumpSum: (data) => run((v) => billApi.boardingLumpSum(b, v, data)),
    finalize:        () => run((v) => billApi.finalize(b, v)),
    regenerate:      (reason) => run((v) => billApi.regenerate(b, v, reason)),
    cancel:          async (reason) => {
      const ok = await run((v) => billApi.cancel(b, v, reason));
      if (ok) {
        setBill(null);
        setNoBill(true);
      }
      return ok;
    },
    /* {date: [{item_id, quantity}]} — one request per date, in date order. */
    saveDays:        async (byDate) => {
      for (const day of Object.keys(byDate).sort()) {
        const ok = await run((v) => billApi.setDay(b, v, day, byDate[day]));
        if (!ok) return false;
      }
      return true;
    },
  };

  const paymentActions = {
    record: async (data) => {
      setBusy(true);
      setError("");
      try {
        const res = await paymentApi.record(b, data);
        setPayments(res.payments.map(mapPayment));
        setPaymentSummary(mapPaymentSummary(res.summary));
        if (versionRef.current != null) setBill(await billApi.get(b));
        return true;
      } catch (e) {
        setError(e.message || "Could not record the payment.");
        return false;
      } finally {
        setBusy(false);
      }
    },
    void: async (paymentId, reason) => {
      setBusy(true);
      setError("");
      try {
        const res = await paymentApi.void(b, paymentId, reason);
        setPayments(res.payments.map(mapPayment));
        setPaymentSummary(mapPaymentSummary(res.summary));
        if (versionRef.current != null) setBill(await billApi.get(b));
        return true;
      } catch (e) {
        setError(e.message || "Could not void the payment.");
        return false;
      } finally {
        setBusy(false);
      }
    },
  };

  return {
    bill, noBill, payments, paymentSummary, loading, busy, error, clearError: () => setError(""),
    reload: load, loadPayments, ...actions, payment: paymentActions,
  };
}
